import { Link } from "react-router-dom";
import { Linkedin, Twitter, Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { COMPANY } from "@/data/site";

const COMPANY_LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Industries", href: "#industries" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Contact", href: "#contact" },
];

const SOLUTION_LINKS = [
  { label: "AI Chatbots", href: "#services" },
  { label: "WhatsApp Automation", href: "#ai-solutions" },
  { label: "AI Voice Agents", href: "#ai-solutions" },
  { label: "Business Automation", href: "#ai-solutions" },
  { label: "Website Development", href: "#services" },
  { label: "App Development", href: "#services" },
];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "#" },
  { icon: Twitter, label: "X (Twitter)", href: "#" },
  { icon: Instagram, label: "Instagram", href: "#" },
  { icon: Facebook, label: "Facebook", href: "#" },
];

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-white/10 bg-navy-950">
    <div className="mx-auto max-w-7xl px-6 py-16 md:px-12 md:py-20">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <a href="#home" data-testid="footer-logo" className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 font-display text-lg font-semibold text-electric-bright ring-1 ring-white/10">
              P
            </span>
            <span className="font-display text-lg font-medium tracking-tight text-white">
              Prata <span className="text-electric-bright">Technologies</span>
            </span>
          </a>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-slate-400">
            AI, automation and digital solutions for modern businesses.
          </p>
          <div className="mt-6 flex gap-3">
            {SOCIALS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                aria-label={`${social.label} (link coming soon)`}
                title={`${social.label} — coming soon`}
                data-testid={`social-${social.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-[color,border-color,background-color] duration-300 hover:border-electric/50 hover:bg-electric/10 hover:text-white"
              >
                <social.icon className="h-4 w-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </div>

        <nav aria-label="Company">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Company</p>
          <ul className="mt-5 space-y-3">
            {COMPANY_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  data-testid={`footer-company-${link.label.toLowerCase()}`}
                  className="text-sm text-slate-400 transition-colors duration-300 hover:text-white"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              <Link
                to="/careers"
                data-testid="footer-company-careers"
                className="text-sm text-slate-400 transition-colors duration-300 hover:text-white"
              >
                Careers
              </Link>
            </li>
          </ul>
        </nav>

        <nav aria-label="Solutions">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Solutions</p>
          <ul className="mt-5 space-y-3">
            {SOLUTION_LINKS.map((link) => (
              <li key={link.label}>
                <a
                  href={link.href}
                  data-testid={`footer-solution-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="text-sm text-slate-400 transition-colors duration-300 hover:text-white"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Contact</p>
          <ul className="mt-5 space-y-4 text-sm text-slate-400">
            <li className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-electric-bright" strokeWidth={1.5} />
              <a href={`mailto:${COMPANY.email}`} data-testid="footer-email" className="transition-colors duration-300 hover:text-white">
                {COMPANY.email}
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-electric-bright" strokeWidth={1.5} />
              <span data-testid="footer-phone">{COMPANY.phone}</span>
            </li>
            <li className="flex items-center gap-3">
              <MapPin className="h-4 w-4 shrink-0 text-electric-bright" strokeWidth={1.5} />
              <span data-testid="footer-address">{COMPANY.address}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-14 flex flex-col items-start justify-between gap-5 border-t border-white/10 pt-8 md:flex-row md:items-center">
        <div className="text-xs leading-relaxed text-slate-500">
          <p data-testid="footer-copyright">
            © 2026 {COMPANY.name}. All rights reserved.
          </p>
          <p className="mt-1">Company Number: {COMPANY.companyNumber}</p>
        </div>
        <div className="flex flex-wrap gap-6">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-xs text-slate-500 transition-colors duration-300 hover:text-white">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" data-testid="footer-cookie-link" className="text-xs text-slate-500 transition-colors duration-300 hover:text-white">
            Cookie Policy
          </Link>
          <Link to="/terms-and-conditions" data-testid="footer-terms-link" className="text-xs text-slate-500 transition-colors duration-300 hover:text-white">
            Terms & Conditions
          </Link>
        </div>
      </div>
    </div>
  </footer>
);

export default Footer;
